import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';
import { blogArticles } from '../../../mocks/blogArticles';
import { articleContents } from '../../../mocks/articleContents';
import { generateSocialGraphic, generateAllPlatformGraphics, downloadGraphic, autoPostToSocial } from '../../../lib/socialGraphics';
import { updateSEO, cleanupStructuredData } from '../../../lib/seo';

export default function ArticlePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const articleId = searchParams.get('id');
  
  const article = blogArticles.find(a => a.id === Number(articleId));
  const content = articleContents.find(c => c.articleId === Number(articleId));
  const [copied, setCopied] = useState(false);

  const [showShareGraphics, setShowShareGraphics] = useState(false);
  const [generatingGraphics, setGeneratingGraphics] = useState(false);
  const [graphics, setGraphics] = useState<any>();
  const [postingToSocial, setPostingToSocial] = useState(false);
  const [postResult, setPostResult] = useState<any>(null);
  const [shareNotification, setShareNotification] = useState<{ show: boolean; message: string; type: 'success' | 'info' | 'error' }>({ show: false, message: '', type: 'info' });

  // Redirect if no article ID or article not found
  useEffect(() => {
    if (!articleId || !article) {
      navigate('/blog', { replace: true });
    }
  }, [articleId, article, navigate]);

  useEffect(() => {
    if (article) {
      const siteUrl = import.meta.env.VITE_SITE_URL || 'https://ranchfireradio.com';
      const articleUrl = `${siteUrl}/blog/article?id=${article.id}`;
      
      updateSEO({
        title: `${article.title} - RanchFire Radio Blog`,
        description: article.excerpt,
        keywords: `${article.category}, Texas Country Music, ${article.title}, RanchFire Radio`,
        canonical: `/blog/article?id=${article.id}`,
        ogTitle: article.title,
        ogDescription: article.excerpt,
        ogImage: article.image,
        ogType: 'article',
        structuredData: {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": article.title,
          "description": article.excerpt,
          "image": article.image,
          "datePublished": article.date,
          "dateModified": article.date,
          "author": {
            "@type": "Organization",
            "name": "RanchFire Radio"
          },
          "publisher": {
            "@type": "Organization",
            "name": "RanchFire Radio",
            "logo": {
              "@type": "ImageObject",
              "url": "https://static.readdy.ai/image/f407d1ad302e1be1c54e67247a4ef201/1d2b59dfb9c0bc727c9fa79ffa1643de.png"
            }
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": articleUrl
          },
          "articleSection": article.category,
          "keywords": `${article.category}, Texas Country Music, ${article.title}`
        }
      });
    }

    return () => {
      cleanupStructuredData();
    };
  }, [article]);

  // Show nothing while redirecting
  if (!articleId || !article || !content) {
    return null;
  }

  const relatedArticles = blogArticles
    .filter(a => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = article?.title || '';
  const shareDescription = article?.excerpt || '';

  const handleGenerateGraphics = async () => {
    if (!article) return;
    
    setGeneratingGraphics(true);
    setShowShareGraphics(true);
    
    const results = await generateAllPlatformGraphics({
      title: article.title,
      excerpt: article.excerpt,
      author: article.author,
      date: article.date,
      image: article.image,
    });
    
    setGraphics(results);
    setGeneratingGraphics(false);
  };

  const handleDownloadGraphic = (platform: string) => {
    if (graphics[platform]?.imageUrl) {
      downloadGraphic(
        graphics[platform].imageUrl,
        `${article?.title.toLowerCase().replace(/\s+/g, '-')}-${platform}.svg`
      );
    }
  };

  const handleAutoPost = async () => {
    if (!article) return;
    
    setPostingToSocial(true);
    setPostResult(null);
    
    const result = await autoPostToSocial({
      title: article.title,
      excerpt: article.excerpt,
      author: article.author,
      date: article.date,
      image: article.image,
      url: `https://ranchfireradio.com/blog/article?id=${article.id}`,
    });
    
    setPostResult(result);
    setPostingToSocial(false);
  };

  const showNotification = (message: string, type: 'success' | 'info' | 'error' = 'info') => {
    setShareNotification({ show: true, message, type });
    setTimeout(() => setShareNotification({ show: false, message: '', type: 'info' }), 4000);
  };

  const shareToFacebook = () => {
    const encodedUrl = encodeURIComponent(shareUrl);
    
    // Use Facebook's Feed Dialog - most reliable method without app_id
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    
    const popup = window.open(
      facebookUrl,
      'facebook-share-dialog',
      'width=626,height=436,menubar=no,toolbar=no,resizable=yes,scrollbars=yes'
    );

    // Check if popup was blocked
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      // Fallback: open in new tab
      window.open(facebookUrl, '_blank');
      showNotification('Opening Facebook share in new tab...');
    } else {
      showNotification('Opening Facebook share dialog...');
    }
  };

  const shareToTwitter = () => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(shareTitle);
    const twitterUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
    
    const popup = window.open(twitterUrl, 'twitter-share', 'width=600,height=400,scrollbars=yes');
    
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      window.open(twitterUrl, '_blank');
      showNotification('Opening X (Twitter) share in a new tab...', 'info');
    } else {
      showNotification('X (Twitter) share dialog opened!', 'success');
    }
  };

  const shareToLinkedIn = () => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
    
    const popup = window.open(linkedInUrl, 'linkedin-share', 'width=600,height=400,scrollbars=yes');
    
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      window.open(linkedInUrl, '_blank');
      showNotification('Opening LinkedIn share in a new tab...', 'info');
    } else {
      showNotification('LinkedIn share dialog opened!', 'success');
    }
  };

  const shareViaEmail = () => {
    const encodedTitle = encodeURIComponent(shareTitle);
    const encodedDescription = encodeURIComponent(shareDescription);
    const encodedUrl = encodeURIComponent(shareUrl);
    window.location.href = `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`;
    showNotification('Opening email client...', 'info');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    showNotification('Link copied to clipboard!', 'success');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareToInstagram = () => {
    // Instagram doesn't support direct web sharing with pre-filled content
    // Open Instagram web with a notification to the user
    const instagramUrl = 'https://www.instagram.com/ranchfire_radio/';
    window.open(instagramUrl, '_blank');
    
    // Copy the article title and URL to clipboard for easy pasting
    const shareText = `${article?.title}\n\n${window.location.href}`;
    navigator.clipboard.writeText(shareText).then(() => {
      showNotification('Article info copied! Paste it into your Instagram post.', 'success');
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Share Notification Toast */}
      {shareNotification.show && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 animate-fade-in">
          <div className={`px-6 py-3 rounded-full shadow-lg flex items-center gap-2 ${
            shareNotification.type === 'success' ? 'bg-green-600 text-white' :
            shareNotification.type === 'error' ? 'bg-red-600 text-white' :
            'bg-gray-800 text-white'
          }`}>
            <i className={`${
              shareNotification.type === 'success' ? 'ri-check-line' :
              shareNotification.type === 'error' ? 'ri-error-warning-line' :
              'ri-information-line'
            }`}></i>
            <span className="font-medium text-sm">{shareNotification.message}</span>
          </div>
        </div>
      )}
      
      {/* Hero Image */}
      <section className="relative pt-24 sm:pt-28">
        <div className="relative w-full h-96 sm:h-[500px] overflow-hidden">
          <img 
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
          
          <div className="absolute bottom-0 left-0 right-0 pb-8 sm:pb-12">
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
              <span className="inline-block bg-orange-600 text-white px-4 py-2 rounded-full text-xs font-bold uppercase mb-4">
                {article.category}
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white uppercase tracking-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-white/90 text-sm">
                <span className="flex items-center gap-2">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-2">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {/* Social Share Buttons - Desktop Floating */}
          <div className="hidden lg:block fixed left-8 top-1/2 -translate-y-1/2 z-40">
            <div className="flex flex-col gap-3">
              <button
                onClick={shareToFacebook}
                className="w-12 h-12 bg-[#1877F2] hover:bg-[#0C63D4] text-white rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg cursor-pointer"
                title="Share on Facebook"
              >
                <i className="ri-facebook-fill text-xl"></i>
              </button>
              <button
                onClick={shareToTwitter}
                className="w-12 h-12 bg-[#000000] hover:bg-[#333333] text-white rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg cursor-pointer"
                title="Share on X (Twitter)"
              >
                <i className="ri-twitter-x-fill text-xl"></i>
              </button>
              <button
                onClick={shareToLinkedIn}
                className="w-12 h-12 bg-[#0A66C2] hover:bg-[#004182] text-white rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg cursor-pointer"
                title="Share on LinkedIn"
              >
                <i className="ri-linkedin-fill text-xl"></i>
              </button>
              <button
                onClick={shareToInstagram}
                className="w-12 h-12 bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90 text-white rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg cursor-pointer"
                title="Share on Instagram"
              >
                <i className="ri-instagram-fill text-xl"></i>
              </button>
              <button
                onClick={shareViaEmail}
                className="w-12 h-12 bg-gray-700 hover:bg-gray-800 text-white rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg cursor-pointer"
                title="Share via Email"
              >
                <i className="ri-mail-fill text-xl"></i>
              </button>
              <button
                onClick={copyLink}
                className="w-12 h-12 bg-orange-600 hover:bg-orange-700 text-white rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg cursor-pointer"
                title="Copy Link"
              >
                <i className={copied ? "ri-check-line text-xl" : "ri-link text-xl"}></i>
              </button>
            </div>
          </div>

          {/* Social Share Buttons - Mobile Top */}
          <div className="lg:hidden sticky top-20 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-200 py-3">
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
              <div className="flex items-center justify-center gap-2 overflow-x-auto">
                <button
                  onClick={shareToFacebook}
                  className="w-10 h-10 bg-[#1877F2] hover:bg-[#0C63D4] text-white rounded-full flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer"
                  title="Share on Facebook"
                >
                  <i className="ri-facebook-fill text-lg"></i>
                </button>
                <button
                  onClick={shareToTwitter}
                  className="w-10 h-10 bg-[#000000] hover:bg-[#333333] text-white rounded-full flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer"
                  title="Share on X"
                >
                  <i className="ri-twitter-x-fill text-lg"></i>
                </button>
                <button
                  onClick={shareToLinkedIn}
                  className="w-10 h-10 bg-[#0A66C2] hover:bg-[#004182] text-white rounded-full flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer"
                  title="Share on LinkedIn"
                >
                  <i className="ri-linkedin-fill text-lg"></i>
                </button>
                <button
                  onClick={shareToInstagram}
                  className="w-10 h-10 bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90 text-white rounded-full flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer"
                  title="Share on Instagram"
                >
                  <i className="ri-instagram-fill text-lg"></i>
                </button>
                <button
                  onClick={shareViaEmail}
                  className="w-10 h-10 bg-gray-700 hover:bg-gray-800 text-white rounded-full flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer"
                  title="Email"
                >
                  <i className="ri-mail-fill text-lg"></i>
                </button>
                <button
                  onClick={copyLink}
                  className="w-10 h-10 bg-orange-600 hover:bg-orange-700 text-white rounded-full flex items-center justify-center transition-all duration-300 flex-shrink-0 cursor-pointer"
                  title="Copy Link"
                >
                  <i className={copied ? "ri-check-line text-lg" : "ri-link text-lg"}></i>
                </button>
              </div>
            </div>
          </div>

          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-8">
            <p className="text-xl text-gray-700 leading-relaxed font-medium">
              {article.excerpt}
            </p>
          </div>

          {/* Main Content */}
          <div className="prose prose-lg max-w-none">
            {content.sections.map((section, index) => (
              <div key={index} className="mb-8">
                <h2 className="text-2xl sm:text-3xl font-black text-gray-900 uppercase tracking-tight mb-4">
                  {section.heading}
                </h2>
                {section.paragraphs.map((paragraph, pIndex) => (
                  <p key={pIndex} className="text-gray-700 leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>
            ))}
          </div>

          {/* Social Media Tools - Moved here for better visibility */}
          <div className="mt-12 mb-8">
            <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-200">
              <h3 className="text-xl font-black text-gray-900 uppercase mb-4 flex items-center gap-2">
                <i className="ri-image-line text-orange-600"></i>
                Social Media Tools
              </h3>
              
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleGenerateGraphics}
                  disabled={generatingGraphics}
                  className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-full font-bold text-sm uppercase tracking-wide transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-image-add-line"></i>
                  {generatingGraphics ? 'Generating...' : 'Generate Graphics'}
                </button>
                
                <button
                  onClick={handleAutoPost}
                  disabled={postingToSocial}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-full font-bold text-sm uppercase tracking-wide transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-send-plane-fill"></i>
                  {postingToSocial ? 'Posting...' : 'Auto-Post to Social'}
                </button>
              </div>

              {/* Post Result */}
              {postResult && (
                <div className={`mt-4 p-4 rounded-lg ${postResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <p className={`font-bold flex items-center gap-2 ${postResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    <i className={`${postResult.success ? 'ri-check-line' : 'ri-error-warning-line'}`}></i>
                    {postResult.success ? 'Posted successfully!' : 'Posting failed'}
                  </p>
                  {postResult.posts && (
                    <ul className="mt-2 space-y-1 text-sm">
                      {postResult.posts.map((post: any, idx: number) => (
                        <li key={idx} className={post.success ? 'text-green-700' : 'text-red-700'}>
                          {post.platform}: {post.success ? '✓ Success' : `✗ ${post.error || 'Failed'}`}
                          {post.withGraphic && ' (with custom graphic)'}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Graphics Preview & Download */}
              {showShareGraphics && Object.keys(graphics).length > 0 && (
                <div className="mt-6 space-y-4">
                  <h4 className="font-bold text-gray-900 uppercase text-sm">Generated Graphics:</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {Object.entries(graphics).map(([platform, data]: [string, any]) => (
                      <div key={platform} className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-bold text-gray-900 uppercase text-sm flex items-center gap-2">
                            <i className={`ri-${platform === 'facebook' ? 'facebook' : platform === 'twitter' ? 'twitter-x' : platform === 'instagram' ? 'instagram' : 'linkedin'}-fill text-${platform === 'facebook' ? 'blue' : platform === 'twitter' ? 'black' : platform === 'instagram' ? 'pink' : 'blue'}-600`}></i>
                            {platform}
                          </span>
                          {data.success && (
                            <button
                              onClick={() => handleDownloadGraphic(platform)}
                              className="text-orange-600 hover:text-orange-700 font-bold text-xs uppercase cursor-pointer flex items-center gap-1"
                            >
                              <i className="ri-download-line"></i>
                              Download
                            </button>
                          )}
                        </div>
                        {data.success && data.imageUrl ? (
                          <img 
                            src={data.imageUrl} 
                            alt={`${platform} graphic`}
                            className="w-full rounded border border-gray-200"
                          />
                        ) : (
                          <div className="bg-gray-100 rounded p-4 text-center text-gray-500 text-sm">
                            Failed to generate
                          </div>
                        )}
                        {data.dimensions && (
                          <p className="text-xs text-gray-500 mt-2">
                            {data.dimensions.width} × {data.dimensions.height}px
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-12 sm:py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 uppercase tracking-tight mb-8 sm:mb-12">
              Related Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {relatedArticles.map((relatedArticle) => (
                <a
                  key={relatedArticle.id}
                  href={`/blog/article?id=${relatedArticle.id}`}
                  className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img 
                      src={relatedArticle.image}
                      alt={relatedArticle.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="bg-orange-600 text-white px-3 py-1 rounded-full text-xs font-bold uppercase">
                        {relatedArticle.category}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-4 sm:p-6">
                    <h3 className="text-gray-900 font-black text-lg mb-2 group-hover:text-orange-600 transition-colors leading-tight">
                      {relatedArticle.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-3">
                      {relatedArticle.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {relatedArticle.date}
                      </span>
                      <i className="ri-arrow-right-line text-orange-600 text-base group-hover:translate-x-2 transition-transform"></i>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}

// Helper function to update meta tags dynamically
function updateMetaTags(data: {
  title: string;
  description: string;
  image: string;
  url: string;
  type: string;
  author?: string;
  publishedTime?: string;
}) {
  // Update or create Open Graph tags
  setMetaTag('og:title', data.title);
  setMetaTag('og:description', data.description);
  setMetaTag('og:image', data.image);
  setMetaTag('og:url', data.url);
  setMetaTag('og:type', data.type);
  setMetaTag('og:image:width', '1200');
  setMetaTag('og:image:height', '630');
  
  // Update Twitter Card tags
  setMetaTag('twitter:card', 'summary_large_image');
  setMetaTag('twitter:title', data.title);
  setMetaTag('twitter:description', data.description);
  setMetaTag('twitter:image', data.image);
  setMetaTag('twitter:url', data.url);
  
  // Article-specific tags
  if (data.author) {
    setMetaTag('article:author', data.author);
  }
  if (data.publishedTime) {
    setMetaTag('article:published_time', data.publishedTime);
  }
  
  // Update canonical URL
  let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  canonical.href = data.url;
}

function setMetaTag(property: string, content: string) {
  const isOg = property.startsWith('og:');
  const isTwitter = property.startsWith('twitter:');
  const isArticle = property.startsWith('article:');
  
  let selector = '';
  if (isOg || isArticle) {
    selector = `meta[property="${property}"]`;
  } else if (isTwitter) {
    selector = `meta[name="${property}"]`;
  }
  
  let meta = document.querySelector(selector) as HTMLMetaElement;
  
  if (!meta) {
    meta = document.createElement('meta');
    if (isOg || isArticle) {
      meta.setAttribute('property', property);
    } else {
      meta.setAttribute('name', property);
    }
    document.head.appendChild(meta);
  }
  
  meta.content = content;
}
